import { LidarZoneSelectionModel } from "../../laserScanScene/types/laserScanScene";
import {
  LidarDataSummaryForVisualization,
  LidarDataSummaryForVisualizationMapped,
  LidarDataVisualizationZone,
} from "../types/getLiDARDataVisualizationSummariesForLastSpecificHeats";

export enum ScatterWorkerTypes {
  GET_PROCESSED_LIDAR_DATA,
}

const ScatterWorker = () => {
  self.onmessage = (e) => {
    const { type, payload } = e.data;

    if (type == ScatterWorkerTypes.GET_PROCESSED_LIDAR_DATA) {
      const { data, bottomColorScale, sidewallColorScale, lastHeatId } =
        payload;

      const result = getMapppedData(
        data,
        bottomColorScale,
        sidewallColorScale,
        lastHeatId,
      );
      self.postMessage({
        type: ScatterWorkerTypes.GET_PROCESSED_LIDAR_DATA,
        payload: result,
      });
    }
  };

  const mapVisualizationData = (
    sidewallData: number[],
    sidewallColorScale: { min: number; max: number; color: string }[],
    bottomData: number[],
    bottomColorScale: { min: number; max: number; color: string }[],
    isLastHeat: boolean,
  ): [
    LidarDataVisualizationZone[],
    LidarDataVisualizationZone[],
    LidarZoneSelectionModel[],
  ] => {
    const sidewallMappedData: LidarDataVisualizationZone[] = [];
    const bottomMappedData: LidarDataVisualizationZone[] = [];
    const lastAggregatedHeatData: LidarZoneSelectionModel[] = [];
    const QUADRANT_ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    //INFO: at bottom data x and y should be swapped
    // const xIndex = isBottom ? 1 : 0;
    // const yIndex = isBottom ? 0 : 1;

    for (let i = 0; i < sidewallData.length; i += 4) {
      const x = sidewallData[i];
      const y = sidewallData[i + 1];
      const thickness = sidewallData[i + 2];

      const quadrant = sidewallData[i + 3];

      const isAboveScaleVal =
        sidewallColorScale[sidewallColorScale.length - 1].max <= thickness;

      const color =
        sidewallColorScale.find(
          (scale) => scale.min <= thickness && scale.max > thickness,
        )?.color ||
        (isAboveScaleVal
          ? sidewallColorScale[sidewallColorScale.length - 1].color
          : sidewallColorScale[0].color);

      const course = calculateSidewallCourse(y);

      sidewallMappedData.push({
        x,
        y,
        thickness,
        quadrant,
        course,
        color: hexToRgb(color),
      });
      if (isLastHeat)
        lastAggregatedHeatData.push({
          clock: QUADRANT_ORDER.indexOf(quadrant),
          mainZoneOrder: 1,
          min: thickness,
          course,
        });
    }

    for (let i = 0; i < bottomData.length; i += 4) {
      const x = bottomData[i + 1];
      const y = bottomData[i];
      const thickness = bottomData[i + 2];
      const quadrant = bottomData[i + 3];

      const isAboveScaleVal =
        bottomColorScale[bottomColorScale.length - 1].max <= thickness;

      const color =
        bottomColorScale.find(
          (scale) => scale.min <= thickness && scale.max > thickness,
        )?.color ||
        (isAboveScaleVal
          ? bottomColorScale[bottomColorScale.length - 1].color
          : bottomColorScale[0].color);

      const course = calculateBottomCourse(x, y);

      bottomMappedData.push({
        x,
        y,
        thickness,
        quadrant,
        course,
        color: hexToRgb(color),
      });
      if (isLastHeat)
        lastAggregatedHeatData.push({
          clock: QUADRANT_ORDER.indexOf(quadrant),
          mainZoneOrder: 0,
          min: thickness,
          course,
        });
    }

    return [sidewallMappedData, bottomMappedData, lastAggregatedHeatData];
  };

  const calculateSidewallCourse = (value: number): number => {
    const min = -13.75;
    const step = 13.75 / 41;
    const scaledValue = Math.round((value - min) / step);
    return scaledValue == 0 ? 1 : scaledValue;
  };

  const calculateBottomCourse = (x: number, y: number) => {
    const bottomRadiusInFeet = 5.5;

    const totalCourse = 10;

    const amountOfIncrease = bottomRadiusInFeet / totalCourse;

    const distanceOfPoint = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));

    let course = Math.ceil(distanceOfPoint / amountOfIncrease);
    if (course == 0) {
      course = 1;
    }
    if (course >= totalCourse) course = totalCourse;

    return course;
  };

  const hexToRgb = (hex: string): [number, number, number] => {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b];
  };
  const getMapppedData = (
    data: LidarDataSummaryForVisualization[],
    bottomColorScale: { min: number; max: number; color: string }[],
    sidewallColorScale: { min: number; max: number; color: string }[],
    lastHeatId: string,
  ) => {
    return data.reduce(
      (acc, summary) => {
        const { sidewall, bottom } = summary.visualizationData;

        const isLastHeatData = summary.campaignHeatId === lastHeatId;

        const [sidewallTempData, bottomTempData, lastAggregatedHeatData] =
          mapVisualizationData(
            sidewall,
            sidewallColorScale,
            bottom,
            bottomColorScale,
            isLastHeatData,
          );
        const baseSummary = {
          campaignHeatId: summary.campaignHeatId,
          heatName: summary.heatName,
          heatNumber: summary.heatNumber,
          liDARDataId: summary.liDARDataId,
          writingDateToFolder: summary.writingDateToFolder,
          liDARMeasurementsCloudFileKey: summary.liDARMeasurementsCloudFileKey,
        };

        acc.sidewallVisulizationSummaries.push({
          ...baseSummary,
          visualizationZoneMappedData: sidewallTempData,
        });

        acc.bottomVisualizationSummaries.push({
          ...baseSummary,
          visualizationZoneMappedData: bottomTempData,
        });

        if (isLastHeatData) acc.lastAggregatedData = lastAggregatedHeatData;
        return acc;
      },
      {
        sidewallVisulizationSummaries:
          [] as LidarDataSummaryForVisualizationMapped[],
        bottomVisualizationSummaries:
          [] as LidarDataSummaryForVisualizationMapped[],
        lastAggregatedData: [] as LidarZoneSelectionModel[],
      },
    );
  };
};

export default ScatterWorker;
